import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Stethoscope, Clock, Shield, Smartphone, MessageCircle, Video, FileText, HeartPulse, ChevronRight, ChevronLeft, CheckCircle2 } from 'lucide-react';
const SpeakToDoctorPage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const images = ['https://uploadthingy.s3.us-west-1.amazonaws.com/4SpwaRdQ2fycsgRtD9iWy5/vfvfv.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/71LHJfrm1M3EgMaBNevw1r/vtgt.png'];
  const nextSlide = () => {
    setCurrentSlide(prev => prev === images.length - 1 ? 0 : prev + 1);
  };
  const prevSlide = () => {
    setCurrentSlide(prev => prev === 0 ? images.length - 1 : prev - 1);
  };
  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 5000);
    return () => clearInterval(interval);
  }, [currentSlide]);
  return <div className="bg-cream min-h-screen" data-id="element-707">
      {/* Section 1: Hero Header with Image Slider */}
      <section className="relative h-[500px] md:h-[600px] overflow-hidden" data-id="element-708">
        {/* Sliding Images */}
        {images.map((image, index) => <div key={index} className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${currentSlide === index ? 'opacity-100 z-10' : 'opacity-0 z-0'}`} data-id="element-709">
            <div className="absolute inset-0 bg-black/50 z-10" data-id="element-710"></div>
            <img src={image} alt={`Slide ${index + 1}`} className="w-full h-full object-cover" data-id="element-711" />
          </div>)}

        {/* Hero Text Content */}
        <div className="absolute inset-0 flex items-center z-20" data-id="element-712">
          <div className="container mx-auto px-4 text-center max-w-4xl" data-id="element-713">
            <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full mb-6 text-sm font-medium text-white" data-id="element-714">
              <Stethoscope size={18} data-id="element-715" />
              <span data-id="element-716">Powered by Preggify</span>
            </div>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight text-white" data-id="element-717">
              Expert Medical Support, Right in Your Pocket.
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-718">
              No more "Google-searching" your symptoms at 2 AM. Get instant,
              24/7 access to licensed medical doctors who understand the unique
              journey of pregnancy and motherhood.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-719">
              <Link to="/offerings" className="bg-brown text-cream px-8 py-4 rounded-full hover:bg-brown-dark transition-colors duration-300 text-center font-semibold text-lg flex items-center justify-center gap-2" data-id="element-720">
                Speak to a Doctor
                <ChevronRight size={20} data-id="element-721" />
              </Link>
            </div>
          </div>
        </div>

        {/* Navigation arrows */}
        <button onClick={prevSlide} className="absolute left-4 top-1/2 -translate-y-1/2 z-30 bg-white/30 hover:bg-white/50 rounded-full p-2 text-white transition-colors duration-300" aria-label="Previous slide" data-id="element-722">
          <ChevronLeft size={24} data-id="element-723" />
        </button>
        <button onClick={nextSlide} className="absolute right-4 top-1/2 -translate-y-1/2 z-30 bg-white/30 hover:bg-white/50 rounded-full p-2 text-white transition-colors duration-300" aria-label="Next slide" data-id="element-724">
          <ChevronRight size={24} data-id="element-725" />
        </button>

        {/* Dots navigation */}
        <div className="absolute bottom-6 left-0 right-0 z-30 flex justify-center gap-2" data-id="element-726">
          {images.map((_, index) => <button key={index} onClick={() => setCurrentSlide(index)} className={`w-3 h-3 rounded-full transition-colors duration-300 ${currentSlide === index ? 'bg-white' : 'bg-white/50'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-727" />)}
        </div>
      </section>

      {/* Section 2: The Problem & Solution */}
      <section className="py-20 bg-white" data-id="element-728">
        <div className="container mx-auto px-4" data-id="element-729">
          <div className="max-w-3xl mx-auto text-center" data-id="element-730">
            <h2 className="text-3xl md:text-4xl font-bold text-brown mb-8" data-id="element-731">
              Because Motherhood Doesn't Follow a 9-to-5 Schedule.
            </h2>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed" data-id="element-732">
              As a busy professional or a dedicated stay-at-home mom, your
              health and your baby's wellness are top priorities. But waiting
              hours in a clinic for a simple question isn't always an option.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed p-6 bg-beige rounded-2xl border border-brown/20 shadow-sm" data-id="element-733">
              <strong className="text-brown block mb-2 text-xl" data-id="element-734">
                Jemi Baby Care × Preggify
              </strong>
              We have partnered with Preggify to bridge the gap between "Career
              and Care." We have integrated a premium telemedicine suite
              directly into our ecosystem, giving you a direct line to clinical
              expertise whenever you need it.
            </p>
          </div>
        </div>
      </section>

      {/* Section 3: What You Get */}
      <section className="py-20 bg-beige" data-id="element-735">
        <div className="container mx-auto px-4" data-id="element-736">
          <div className="text-center mb-16" data-id="element-737">
            <h2 className="text-3xl md:text-4xl font-bold text-brown mb-4" data-id="element-738">
              Comprehensive Care at Your Fingertips
            </h2>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto" data-id="element-739">
              Everything you need to feel secure and supported, anytime,
              anywhere.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-740">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-brown/10 hover:shadow-md transition-shadow" data-id="element-741">
              <div className="w-14 h-14 bg-brown/10 rounded-full flex items-center justify-center mb-6" data-id="element-742">
                <Clock className="text-brown" size={28} data-id="element-743" />
              </div>
              <h3 className="text-xl font-bold text-brown mb-3" data-id="element-744">
                24/7 Virtual Consultations
              </h3>
              <p className="text-gray-700" data-id="element-745">
                Speak to a licensed doctor via chat or call, day or night. Never
                wait for office hours again.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-brown/10 hover:shadow-md transition-shadow" data-id="element-746">
              <div className="w-14 h-14 bg-brown/10 rounded-full flex items-center justify-center mb-6" data-id="element-747">
                <HeartPulse className="text-brown" size={28} data-id="element-748" />
              </div>
              <h3 className="text-xl font-bold text-brown mb-3" data-id="element-749">
                Pregnancy & Postpartum Expertise
              </h3>
              <p className="text-gray-700" data-id="element-750">
                Specialist advice on everything from morning sickness and fetal
                movements to postpartum recovery and neonatal care.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-brown/10 hover:shadow-md transition-shadow" data-id="element-751">
              <div className="w-14 h-14 bg-brown/10 rounded-full flex items-center justify-center mb-6" data-id="element-752">
                <FileText className="text-brown" size={28} data-id="element-753" />
              </div>
              <h3 className="text-xl font-bold text-brown mb-3" data-id="element-754">
                Prescription & Referral Services
              </h3>
              <p className="text-gray-700" data-id="element-755">
                Get digital prescriptions sent directly to your phone or
                referrals to specialists if physical intervention is required.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-brown/10 hover:shadow-md transition-shadow" data-id="element-756">
              <div className="w-14 h-14 bg-brown/10 rounded-full flex items-center justify-center mb-6" data-id="element-757">
                <Clock className="text-brown" size={28} data-id="element-758" />
              </div>
              <h3 className="text-xl font-bold text-brown mb-3" data-id="element-759">
                Zero Wait Times
              </h3>
              <p className="text-gray-700" data-id="element-760">
                Skip the hospital queues. Get answers in minutes, not hours,
                from the comfort of your home.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: How It Works */}
      <section className="py-20 bg-white" data-id="element-761">
        <div className="container mx-auto px-4" data-id="element-762">
          <h2 className="text-3xl md:text-4xl font-bold text-brown mb-16 text-center" data-id="element-763">
            How It Works
          </h2>

          <div className="max-w-4xl mx-auto relative" data-id="element-764">
            {/* Connecting line for desktop */}
            <div className="hidden md:block absolute top-1/2 left-0 w-full h-1 bg-beige -translate-y-1/2 z-0" data-id="element-765"></div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative z-10" data-id="element-766">
              <div className="bg-cream p-6 rounded-2xl text-center border border-brown/10 relative" data-id="element-767">
                <div className="w-12 h-12 bg-brown text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4 shadow-md" data-id="element-768">
                  1
                </div>
                <h3 className="font-bold text-brown mb-2" data-id="element-769">Join Jemi</h3>
                <p className="text-sm text-gray-700" data-id="element-770">
                  Subscribe to any of our support packages or the CEO Mom
                  Mastermind.
                </p>
              </div>

              <div className="bg-cream p-6 rounded-2xl text-center border border-brown/10 relative" data-id="element-771">
                <div className="w-12 h-12 bg-brown text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4 shadow-md" data-id="element-772">
                  2
                </div>
                <h3 className="font-bold text-brown mb-2" data-id="element-773">
                  Receive Access Code
                </h3>
                <p className="text-sm text-gray-700" data-id="element-774">
                  Check your welcome email for your unique Jemi-Preggify Partner
                  Code.
                </p>
              </div>

              <div className="bg-cream p-6 rounded-2xl text-center border border-brown/10 relative" data-id="element-775">
                <div className="w-12 h-12 bg-brown text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4 shadow-md" data-id="element-776">
                  3
                </div>
                <h3 className="font-bold text-brown mb-2" data-id="element-777">Download App</h3>
                <p className="text-sm text-gray-700" data-id="element-778">
                  Download the Preggify App from the Google Play Store or Apple
                  App Store.
                </p>
              </div>

              <div className="bg-cream p-6 rounded-2xl text-center border border-brown/10 relative" data-id="element-779">
                <div className="w-12 h-12 bg-brown text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4 shadow-md" data-id="element-780">
                  4
                </div>
                <h3 className="font-bold text-brown mb-2" data-id="element-781">Consult</h3>
                <p className="text-sm text-gray-700" data-id="element-782">
                  Log in, enter your code, and you'll be instantly connected to
                  a medical professional.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: Expert Care, On the Go */}
      <section className="py-20 bg-brown text-white" data-id="element-783">
        <div className="container mx-auto px-4" data-id="element-784">
          <div className="flex flex-col md:flex-row items-center gap-12 max-w-5xl mx-auto" data-id="element-785">
            <div className="md:w-1/2" data-id="element-786">
              <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-787">
                Expert Care, On the Go
              </h2>
              <p className="text-lg text-cream/90 mb-8" data-id="element-788">
                To provide you with the most secure and high-quality video/audio
                consultation experience, our medical services, Powered by
                Preggify are hosted on the Preggify Mobile App.
              </p>

              <div className="space-y-6" data-id="element-789">
                <div className="flex items-start gap-4" data-id="element-790">
                  <div className="bg-white/20 p-3 rounded-full" data-id="element-791">
                    <Shield size={24} className="text-white" data-id="element-792" />
                  </div>
                  <div data-id="element-793">
                    <h3 className="text-xl font-bold mb-2" data-id="element-794">Secure & Private</h3>
                    <p className="text-cream/80" data-id="element-795">
                      The app uses end-to-end encryption to keep your health
                      records safe and strictly confidential.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-796">
                  <div className="bg-white/20 p-3 rounded-full" data-id="element-797">
                    <Smartphone size={24} className="text-white" data-id="element-798" />
                  </div>
                  <div data-id="element-799">
                    <h3 className="text-xl font-bold mb-2" data-id="element-800">Health Tracking</h3>
                    <p className="text-cream/80" data-id="element-801">
                      Use the Preggify app's built-in tools to track your
                      pregnancy milestones alongside your support on Jemi Baby
                      Care.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="md:w-1/2 flex justify-center" data-id="element-802">
              <div className="bg-white/10 p-8 rounded-3xl border border-white/20 backdrop-blur-sm" data-id="element-803">
                <div className="flex flex-col gap-6" data-id="element-804">
                  <div className="flex items-center gap-4 bg-white text-brown p-4 rounded-xl shadow-lg" data-id="element-805">
                    <Video size={24} className="text-brown" data-id="element-806" />
                    <span className="font-semibold" data-id="element-807">
                      High-Quality Video Calls
                    </span>
                  </div>
                  <div className="flex items-center gap-4 bg-white text-brown p-4 rounded-xl shadow-lg ml-8" data-id="element-808">
                    <MessageCircle size={24} className="text-brown" data-id="element-809" />
                    <span className="font-semibold" data-id="element-810">Instant Secure Chat</span>
                  </div>
                  <div className="flex items-center gap-4 bg-white text-brown p-4 rounded-xl shadow-lg" data-id="element-811">
                    <FileText size={24} className="text-brown" data-id="element-812" />
                    <span className="font-semibold" data-id="element-813">Digital Prescriptions</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 6: FAQ */}
      <section className="py-20 bg-cream" data-id="element-814">
        <div className="container mx-auto px-4 max-w-4xl" data-id="element-815">
          <h2 className="text-3xl md:text-4xl font-bold text-brown mb-12 text-center" data-id="element-816">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6" data-id="element-817">
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-brown/10" data-id="element-818">
              <h3 className="text-xl font-bold text-brown mb-3 flex items-center gap-2" data-id="element-819">
                <CheckCircle2 size={20} className="text-brown" data-id="element-820" />
                Is this service included in my Jemi subscription?
              </h3>
              <p className="text-gray-700 ml-7" data-id="element-821">
                Yes! It is now a core feature of our holistic support packages.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-brown/10" data-id="element-822">
              <h3 className="text-xl font-bold text-brown mb-3 flex items-center gap-2" data-id="element-823">
                <CheckCircle2 size={20} className="text-brown" data-id="element-824" />
                Why do I need to download another app?
              </h3>
              <p className="text-gray-700 ml-7" data-id="element-825">
                To ensure your medical data remains private and the video/chat
                connection is stable, we use Preggify's dedicated clinical
                infrastructure. The app is lightweight, secure, and designed
                specifically for maternal health.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-brown/10" data-id="element-826">
              <h3 className="text-xl font-bold text-brown mb-3 flex items-center gap-2" data-id="element-827">
                <CheckCircle2 size={20} className="text-brown" data-id="element-828" />
                Is my data safe?
              </h3>
              <p className="text-gray-700 ml-7" data-id="element-829">
                Absolutely. Your medical consultations are private, encrypted,
                and compliant with the Nigeria Data Protection Act.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-brown/10" data-id="element-830">
              <h3 className="text-xl font-bold text-brown mb-3 flex items-center gap-2" data-id="element-831">
                <CheckCircle2 size={20} className="text-brown" data-id="element-832" />
                What if I have an emergency?
              </h3>
              <p className="text-gray-700 ml-7" data-id="element-833">
                While our doctors are available 24/7 for consultations, in the
                event of a life-threatening emergency, please visit the nearest
                hospital immediately.
              </p>
            </div>
          </div>

          <div className="mt-16 text-center" data-id="element-834">
            <Link to="/offerings" className="inline-block bg-brown text-cream px-10 py-4 rounded-full hover:bg-brown-dark transition-colors duration-300 font-semibold text-lg shadow-md" data-id="element-835">
              Speak to a Doctor
            </Link>
          </div>
        </div>
      </section>

      {/* Section 7: The Legal Shield */}
      <section className="py-12 bg-gray-100 border-t border-gray-200" data-id="element-836">
        <div className="container mx-auto px-4 max-w-5xl text-center" data-id="element-837">
          <h4 className="font-bold text-gray-600 mb-2 text-sm uppercase tracking-wider" data-id="element-838">
            Medical Disclaimer
          </h4>
          <p className="text-sm text-gray-500 leading-relaxed" data-id="element-839">
            Telemedicine services are provided exclusively by our licensed
            partner, Preggify. Jemi Baby Care is a lifestyle and
            wellness/coaching platform and does not provide clinical diagnosis
            or medical treatment. Consultations are intended for advisory
            purposes and do not replace the need for physical examinations when
            recommended by a physician.
          </p>
        </div>
      </section>
    </div>;
};
export default SpeakToDoctorPage;